/**
 * Single source of truth for chart + accent colors and shared Recharts
 * styling. Tailwind utility classes cover the DOM; Recharts needs raw
 * values, so everything SVG-side imports from here instead of scattering
 * hex literals per component. Pick the variant for the active mode via
 * `chartThemes[useTheme().theme]` (see src/themeContext.tsx).
 *
 * Hexes intentionally mirror the Tailwind palette (emerald-500 etc.) so
 * DOM accents and chart marks always agree.
 */

export const chartColors = {
  emerald: "#10b981", // emerald-500 — primary accent / base game
  amber: "#f59e0b", // amber-500 — secondary series / CPU fallback
  sky: "#0ea5e9", // sky-500 — per-rule bars / cloud
  pink: "#ec4899", // pink-500 — feature triggers
  violet: "#8b5cf6", // violet-500
  teal: "#14b8a6", // teal-500
  red: "#ef4444", // red-500
  slate: "#94a3b8", // slate-400 — neutral fallback
} as const;

/** Palette for per-feature bonus segments (base is always emerald).
 *  Cycles if a game has more features than colors. */
export const FEATURE_COLORS: readonly string[] = [
  chartColors.amber,
  chartColors.sky,
  chartColors.pink,
  chartColors.violet,
  chartColors.teal,
  chartColors.red,
];

export function featureColor(i: number): string {
  return FEATURE_COLORS[i % FEATURE_COLORS.length] ?? chartColors.slate;
}

export interface ChartTheme {
  /** CartesianGrid stroke. */
  grid: string;
  /** Axis line stroke. */
  axis: string;
  /** Tick props — fontSize + fill. */
  tick: { fontSize: number; fill: string };
  /** Tooltip hover-cursor fill on bar charts. */
  cursorFill: string;
  /** Tooltip container. */
  tooltipContentStyle: React.CSSProperties;
  /** Tooltip label (top line) color. */
  tooltipLabelStyle: React.CSSProperties;
  /** Tooltip item rows color. */
  tooltipItemStyle: React.CSSProperties;
  /** Indigo series accent — indigo-500 on light, indigo-400 on dark
   *  (the 500 reads too dim against slate-950). */
  indigo: string;
}

/** Recharts styling per theme mode. */
export const chartThemes: Record<"light" | "dark", ChartTheme> = {
  light: {
    grid: "#e7e5de", // paper hairline (slightly lighter than border)
    axis: "#d6d3cb", // paper-border
    tick: { fontSize: 10, fill: "#8a8578" }, // paper-label
    cursorFill: "rgba(138, 133, 120, 0.08)",
    tooltipContentStyle: {
      backgroundColor: "#faf9f5", // paper-bg
      border: "1px solid #d6d3cb", // paper-border
      borderRadius: 0,
      fontSize: 12,
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    },
    tooltipLabelStyle: { color: "#292524" }, // paper-bright
    tooltipItemStyle: { color: "#57534e" }, // paper-text
    indigo: "#6366f1", // indigo-500
  },
  dark: {
    grid: "#1e293b", // term-border
    axis: "#334155", // term-hint
    tick: { fontSize: 10, fill: "#94a3b8" }, // term-text
    cursorFill: "rgba(148, 163, 184, 0.08)",
    tooltipContentStyle: {
      backgroundColor: "#0d1320", // term-panel
      border: "1px solid #334155",
      borderRadius: 0,
      fontSize: 12,
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    },
    tooltipLabelStyle: { color: "#e2e8f0" }, // term-bright
    tooltipItemStyle: { color: "#cbd5e1" },
    indigo: "#818cf8", // indigo-400
  },
};
